import { useEffect } from "react";

/**
 * Maintains <title>, meta description, canonical, OpenGraph + Twitter card tags,
 * and one or many JSON-LD schema blocks. All inserted tags are tagged with
 * data-ivtf-seo so we can cleanly remove them on unmount and avoid duplicates.
 */
const TAG = "data-ivtf-seo";

function upsertMeta(attr, key, content) {
  if (!content) return null;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(TAG, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(TAG, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogType = "website",
  ogLocale = "en_US",
  fbAppId,
  siteName = "IV Therapy Finder",
  twitterCard = "summary_large_image",
  twitterSite,
  jsonLd, // single object OR array of objects
}) {
  useEffect(() => {
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertLink("canonical", canonical);

    // Open Graph (Facebook + LinkedIn + Pinterest read these)
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:locale", ogLocale);
    upsertMeta("property", "og:image", ogImage);
    if (ogImage) {
      upsertMeta("property", "og:image:width", String(ogImageWidth));
      upsertMeta("property", "og:image:height", String(ogImageHeight));
      upsertMeta("property", "og:image:alt", title);
    }
    if (fbAppId) upsertMeta("property", "fb:app_id", fbAppId);

    // Twitter / X
    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    if (twitterSite) upsertMeta("name", "twitter:site", twitterSite);

    // JSON-LD (one or many blocks)
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const ldEls = blocks
      .filter(Boolean)
      .map((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute(TAG, "1");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
        return s;
      });

    return () => {
      ldEls.forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, ogImageWidth, ogImageHeight, ogType, ogLocale, fbAppId, siteName, twitterCard, twitterSite, JSON.stringify(jsonLd)]);

  return null;
}
